import { useEffect, useRef, useState } from 'preact/hooks'
import { apiBibliotheque } from './formulaires.tsx'
import { SubdivisionInventaire } from './subdivision-inventaire.tsx'
import {
  TYPES_INVENTAIRE,
  UNITES_INVENTAIRE,
  prixInventaire,
  type CompteGlItem,
  type DonneesInventaire,
  type FicheInventaire,
  type ItemInventaire,
} from './inventaire-types.ts'
const JOURS = [
  { cle: 'lundi', nom: 'Lun' },
  { cle: 'mardi', nom: 'Mar' },
  { cle: 'mercredi', nom: 'Mer' },
  { cle: 'jeudi', nom: 'Jeu' },
  { cle: 'vendredi', nom: 'Ven' },
  { cle: 'samedi', nom: 'Sam' },
  { cle: 'dimanche', nom: 'Dim' },
] as const
const disponibilitesInitiales = {
  jours: JOURS.map((j) => j.cle),
  ouverture: '08:00',
  fermeture: '22:00',
  duree_minimum: 60,
  duree_maximum: 240,
  pas_minutes: 30,
  horizon_jours: 90,
  date: undefined,
  debut: '18:00',
  fin: '20:00',
  capacite: 30,
  maximum_par_inscription: 1,
}
const nouvelleFiche = (): FicheInventaire => ({
  nom: '',
  description: '',
  categorie: '',
  type: 'espace',
  nature: 'location',
  prix_cents: 0,
  unite: 'heure',
  quantite: 1,
  actif: true,
  disponibilites: { ...disponibilitesInitiales },
})
const normaliserRecherche = (texte: string) =>
  texte
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase()
    .trim()
export function IconeInventaire({
  type,
  taille = 24,
}: {
  type: FicheInventaire['type']
  taille?: number
}) {
  return (
    <svg
      width={taille}
      height={taille}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="1.6"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      {type === 'espace' ? (
        <>
          <path d="m3 10 9-7 9 7v11H3Z" />
          <path d="M9 21v-8h6v8" />
        </>
      ) : type === 'equipement' ? (
        <>
          <rect x="3" y="6" width="18" height="12" rx="3" />
          <path d="M8 6V3h8v3M8 18v3m8-3v3M7 11h2m6 0h2" />
        </>
      ) : type === 'produit' ? (
        <>
          <path d="m3 7 9-4 9 4v11l-9 4-9-4Z" />
          <path d="m3 7 9 4 9-4M12 11v11m-5-17 10 4" />
        </>
      ) : (
        <>
          <rect x="3" y="5" width="18" height="16" rx="3" />
          <path d="M7 3v4m10-4v4M3 10h18m-13 5h2m4 0h2" />
        </>
      )}
    </svg>
  )
}
export function InventairePane({
  baseUrl = '',
  permissions,
  onAuth,
  visible = true,
  selectionInitiale,
  onParcours,
  onOuvrirParcours,
}: {
  baseUrl?: string
  permissions: string[]
  onAuth?(): void
  visible?: boolean
  selectionInitiale?: string
  onParcours(item: ItemInventaire): void
  onOuvrirParcours(cle: string): void
}) {
  const [donnees, setDonnees] = useState<DonneesInventaire>({
      items: [],
      comptes: [],
      relations: [],
    }),
    [charge, setCharge] = useState(true),
    [erreur, setErreur] = useState(''),
    [notice, setNotice] = useState(''),
    [occupe, setOccupe] = useState(false)
  const [edition, setEdition] = useState<{
      item: ItemInventaire | null
      fiche: FicheInventaire
    } | null>(null),
    [modifie, setModifie] = useState(false),
    [vueListe, setVueListe] = useState(false),
    [onglet, setOnglet] = useState<'fiche' | 'disponibilites' | 'composition'>(
      'fiche',
    )
  const [recherche, setRecherche] = useState(''),
    [type, setType] = useState('tous'),
    [archives, setArchives] = useState(false),
    [parties, setParties] = useState<string[]>([]),
    [liaisonOuverte, setLiaisonOuverte] = useState(false),
    [rechercheEspaces, setRechercheEspaces] = useState(''),
    [modeLien, setModeLien] = useState<'journee' | 'chevauchement'>('chevauchement')
  const modifiable = permissions.includes('services.modifier'),
    comptable = permissions.includes('finances.modifier')
  const requete = useRef(0),
    ouverture = useRef('')
  const faute = (e: unknown) => {
    if ((e as { statut?: number }).statut === 401) onAuth?.()
    setErreur((e as Error).message)
  }
  async function charger() {
    const n = ++requete.current
    try {
      const d = await apiBibliotheque<DonneesInventaire>(baseUrl, 'inventaire')
      if (n === requete.current) {
        setDonnees(d)
        setCharge(false)
      }
      return d
    } catch (e) {
      faute(e)
      setCharge(false)
      return null
    }
  }
  useEffect(() => {
    if (visible) void charger()
  }, [baseUrl, visible])
  useEffect(() => {
    if (!visible) {
      ouverture.current = ''
      return
    }
    if (
      visible &&
      selectionInitiale &&
      ouverture.current !== selectionInitiale
    ) {
      const i = donnees.items.find((i) => i.id === selectionInitiale)
      if (i) {
        ouvrir(i)
        ouverture.current = selectionInitiale
      }
    }
  }, [visible, selectionInitiale, donnees.items])
  useEffect(() => {
    const avertir = (e: BeforeUnloadEvent) => {
      if (modifie) e.preventDefault()
    }
    window.addEventListener('beforeunload', avertir)
    return () => window.removeEventListener('beforeunload', avertir)
  }, [modifie])
  const changer = (valeurs: Partial<FicheInventaire>) => {
    setEdition((e) => (e ? { ...e, fiche: { ...e.fiche, ...valeurs } } : e))
    setModifie(true)
    setNotice('')
  }
  function ouvrir(item?: ItemInventaire, tab: typeof onglet = 'fiche') {
    if (
      modifie &&
      !window.confirm(
        'Remplacer les modifications non enregistrées de l’élément en cours ?',
      )
    )
      return
    setEdition({
      item: item ?? null,
      fiche: item ? structuredClone(item.fiche) : nouvelleFiche(),
    })
    setModifie(false)
    setVueListe(false)
    setOnglet(tab)
    setParties([])
    setLiaisonOuverte(false)
    setRechercheEspaces('')
    setErreur('')
    setNotice('')
  }
  async function enregistrer() {
    if (!edition || occupe) return
    setOccupe(true)
    setErreur('')
    try {
      if (!edition.fiche.nom.trim())
        throw new Error('Donnez un nom à cet élément.')
      if (edition.fiche.compte_gl && !edition.fiche.compte_gl.compte_debit)
        throw new Error(
          'Choisissez le compte d’encaissement pour compléter la ligne GL.',
        )
      if (
        edition.fiche.compte_gl &&
        donnees.taxes?.some((t) => !edition.fiche.compte_gl?.taxes[t.nom])
      )
        throw new Error(
          'Associez un compte GL à chaque taxe de l’organisation.',
        )
      const reponse = await apiBibliotheque<ItemInventaire>(
        baseUrl,
        'inventaire' + (edition.item ? '/' + edition.item.id : ''),
        edition.item ? 'PUT' : 'POST',
        {
          fiche: edition.fiche,
          ...(edition.item ? { version_id: edition.item.version_id } : {}),
        },
      )
      setEdition({ item: reponse, fiche: reponse.fiche })
      setModifie(false)
      setNotice(
        'L’élément est enregistré. Vous pouvez maintenant créer son parcours de réservation.',
      )
      await charger()
    } catch (e) {
      faute(e)
    } finally {
      setOccupe(false)
    }
  }
  async function associer() {
    if (!edition?.item || !parties.length) return
    setOccupe(true)
    setErreur('')
    try {
      for (const id of parties) {
        if (
          !donnees.relations.some(
            (r) =>
              (r.item_a === edition.item!.id && r.item_b === id) ||
              (r.item_b === edition.item!.id && r.item_a === id),
          )
        )
          await apiBibliotheque(baseUrl, 'inventaire/relations', 'POST', {
            item_a: edition.item.id,
            item_b: id,
            mode: modeLien,
          })
      }
      setParties([])
      setLiaisonOuverte(false)
      setRechercheEspaces('')
      setNotice(
        'Les liens sont enregistrés. Les disponibilités en tiennent compte dans tous les parcours.',
      )
    } catch (e) {
      faute(e)
    } finally {
      await charger()
      setOccupe(false)
    }
  }
  async function retirerLien(id: string) {
    if (
      !window.confirm(
        'Retirer ce lien entre les espaces ? Les réservations existantes restent conservées.',
      )
    )
      return
    setOccupe(true)
    try {
      await apiBibliotheque(baseUrl, 'inventaire/relations/' + id, 'DELETE')
      await charger()
      setNotice('Le lien a été retiré.')
    } catch (e) {
      faute(e)
    } finally {
      setOccupe(false)
    }
  }
  const f = edition?.fiche,
    dispo = { ...disponibilitesInitiales, ...f?.disponibilites }
  const changerDispo = (v: Partial<typeof dispo>) => {
    const prochaine = { ...dispo, ...v }
    if (v.duree_minimum !== undefined || v.duree_maximum !== undefined) {
      prochaine.duree_maximum = Math.max(
        prochaine.duree_minimum,
        prochaine.duree_maximum,
      )
      // Garder la durée choisie et adapter le pas à un diviseur commun.
      const pgcd = (a: number, b: number): number => (b ? pgcd(b, a % b) : a)
      prochaine.pas_minutes = Math.max(
        5,
        pgcd(
          pgcd(prochaine.duree_minimum, prochaine.duree_maximum),
          prochaine.pas_minutes,
        ),
      )
    }
    if (
      v.pas_minutes !== undefined ||
      v.duree_minimum !== undefined ||
      v.duree_maximum !== undefined
    ) {
      const pas = prochaine.pas_minutes,
        limite = Math.floor(1440 / pas) * pas
      prochaine.duree_minimum = Math.min(
        limite,
        Math.ceil(prochaine.duree_minimum / pas) * pas,
      )
      prochaine.duree_maximum = Math.min(
        limite,
        Math.max(
          prochaine.duree_minimum,
          Math.ceil(prochaine.duree_maximum / pas) * pas,
        ),
      )
    }
    changer({ disponibilites: prochaine })
  }
  const changerGL = (v: Partial<CompteGlItem>) => {
    if (!f) return
    changer({
      compte_gl: {
        compte_debit: '',
        compte_credit: '',
        traitement: 'produit',
        taxes: {},
        ...f.compte_gl,
        ...v,
      },
    })
  }
  const items = donnees.items.filter(
    (i) =>
      (archives ? !i.fiche.actif : i.fiche.actif) &&
      (type === 'tous' || i.fiche.type === type) &&
      `${i.fiche.nom} ${i.fiche.categorie ?? ''} ${i.fiche.description}`
        .toLocaleLowerCase()
        .includes(recherche.toLocaleLowerCase()),
  )
  const comptesActifs = donnees.comptes.filter((c) => c.actif),
    relations = donnees.relations.filter(
      (r) => r.item_a === edition?.item?.id || r.item_b === edition?.item?.id,
    )
  const espacesALier = donnees.items.filter(
    (i) =>
      i.id !== edition?.item?.id &&
      i.fiche.type === 'espace' &&
      i.fiche.actif &&
      !relations.some((r) => r.item_a === i.id || r.item_b === i.id) &&
      normaliserRecherche(`${i.fiche.nom} ${i.fiche.categorie ?? ''}`).includes(
        normaliserRecherche(rechercheEspaces),
      ),
  )
  return (
    <div class="iv-root">
      <style>{stylesInventaire}</style>
      {erreur ? (
        <p class="iv-alert" role="alert">
          {erreur}
        </p>
      ) : null}
      {notice ? (
        <p class="iv-notice" role="status">
          {notice}
        </p>
      ) : null}
      {!edition || vueListe ? (
        <>
          <div class="iv-entete">
            <div>
              <h2>Vos éléments à réserver.</h2>
              <p>
                Salles, terrains et équipements : chaque élément se réserve pour
                une période, selon sa capacité disponible.
              </p>
            </div>
            {modifiable ? (
              <button type="button" class="iv-primary" onClick={() => ouvrir()}>
                + Ajouter un élément
              </button>
            ) : null}
          </div>
          {edition && modifie ? (
            <div class="iv-reprise">
              <span>Les modifications de votre élément sont conservées.</span>
              <button type="button" onClick={() => setVueListe(false)}>
                Reprendre
              </button>
            </div>
          ) : null}
          <div class="iv-resume">
            <div>
              <strong>
                {donnees.items.filter((i) => i.fiche.actif).length}
              </strong>
              <span>éléments actifs</span>
            </div>
            <div>
              <strong>
                {
                  donnees.items.filter(
                    (i) => i.fiche.actif && i.services.length,
                  ).length
                }
              </strong>
              <span>liés à un parcours</span>
            </div>
            <div>
              <strong>
                {
                  donnees.items.filter(
                    (i) =>
                      i.fiche.actif &&
                      i.fiche.prix_cents > 0 &&
                      !i.fiche.compte_gl?.compte_credit,
                  ).length
                }
              </strong>
              <span>comptes GL à associer</span>
            </div>
          </div>
          <div class="iv-outils">
            <label class="iv-recherche">
              <span>Rechercher</span>
              <input
                type="search"
                placeholder="Nom, catégorie, équipement…"
                value={recherche}
                onInput={(e) => setRecherche(e.currentTarget.value)}
              />
            </label>
            <label>
              <span>Type d’élément</span>
              <select
                aria-label="Type d’élément"
                value={type}
                onChange={(e) => setType(e.currentTarget.value)}
              >
                <option value="tous">Tous les types</option>
                {Object.entries(TYPES_INVENTAIRE).map(([k, v]) => (
                  <option value={k}>{v}</option>
                ))}
              </select>
            </label>
            <label class="iv-checkbox">
              <input
                type="checkbox"
                checked={archives}
                onChange={(e) => setArchives(e.currentTarget.checked)}
              />
              Inactifs
            </label>
          </div>
          {charge ? (
            <p class="iv-vide" role="status">
              Chargement de l’inventaire…
            </p>
          ) : items.length ? (
            <div class="iv-table-wrap">
              <table class="iv-table">
                <thead>
                  <tr>
                    <th>Élément</th>
                    <th>Prix de référence</th>
                    <th>Capacité / période</th>
                    <th>Compte GL</th>
                    <th>Parcours</th>
                    <th>
                      <span class="iv-sr">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((i) => (
                    <tr key={i.id}>
                      <td>
                        <button
                          type="button"
                          class="iv-article"
                          onClick={() => ouvrir(i)}
                        >
                          <span class={'iv-icone iv-icone-' + i.fiche.type}>
                            <IconeInventaire type={i.fiche.type} />
                          </span>
                          <span>
                            <strong>{i.fiche.nom}</strong>
                            <small>
                              {TYPES_INVENTAIRE[i.fiche.type]}
                              {i.fiche.categorie
                                ? ' · ' + i.fiche.categorie
                                : ''}
                            </small>
                          </span>
                        </button>
                      </td>
                      <td>
                        <strong>
                          {i.fiche.prix_cents
                            ? prixInventaire(i.fiche.prix_cents)
                            : 'Gratuit'}
                        </strong>
                        <small>{UNITES_INVENTAIRE[i.fiche.unite]}</small>
                      </td>
                      <td>
                        <span>{i.fiche.quantite}</span>
                        <small>
                          {i.fiche.nature === 'achat'
                            ? 'max. par achat'
                            : i.fiche.type === 'activite'
                              ? 'places'
                              : i.fiche.type === 'espace'
                                ? 'espace'
                                : 'unités'}
                          {i.fiche.nature !== 'achat' ? ' par créneau' : ''}
                        </small>
                        {i.fiche.nature !== 'achat' ? (
                          <button
                            type="button"
                            class="iv-link iv-horaires"
                            aria-label={'Horaires de ' + i.fiche.nom}
                            onClick={() => ouvrir(i, 'disponibilites')}
                          >
                            Horaires ↗
                          </button>
                        ) : null}
                      </td>
                      <td>
                        <button
                          type="button"
                          class={
                            'iv-compte ' +
                            (!i.fiche.compte_gl?.compte_credit
                              ? 'iv-a-completer'
                              : '')
                          }
                          onClick={() => ouvrir(i)}
                        >
                          {i.fiche.compte_gl?.compte_credit ??
                            'Associer un compte'}
                        </button>
                        <small>
                          {
                            donnees.comptes.find(
                              (c) =>
                                c.code === i.fiche.compte_gl?.compte_credit,
                            )?.libelle
                          }
                        </small>
                      </td>
                      <td>
                        {i.services.length ? (
                          <button
                            type="button"
                            class="iv-link"
                            onClick={() => onOuvrirParcours(i.services[0]!.cle)}
                          >
                            {i.services.length} parcours
                          </button>
                        ) : (
                          <span class="iv-muted">À proposer</span>
                        )}
                      </td>
                      <td>
                        {modifiable ? (
                          <button
                            type="button"
                            class="iv-button iv-petit"
                            onClick={() => onParcours(i)}
                          >
                            Créer un parcours →
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div class="iv-vide">
              <h3>
                {recherche
                  ? 'Aucun élément trouvé'
                  : 'Votre inventaire commence ici'}
              </h3>
              <p>
                {recherche
                  ? 'Essayez un autre nom ou une autre catégorie.'
                  : 'Ajoutez une salle, un terrain ou un équipement, réglez ses horaires, puis composez son parcours de réservation.'}
              </p>
              {modifiable && !recherche ? (
                <button
                  type="button"
                  class="iv-primary"
                  onClick={() => ouvrir()}
                >
                  Ajouter un élément
                </button>
              ) : null}
            </div>
          )}
          <div class="iv-explication">
            <span class="iv-icone">
              <IconeInventaire type="espace" />
            </span>
            <div>
              <strong>Une salle entière, deux demi-salles ?</strong>
              <p>
                Ouvrez la salle complète, puis Subdivisions et liens. Créez ou reliez ses sections :
                une moitié occupée bloque l’ensemble, tout en laissant l’autre
                moitié disponible.
              </p>
            </div>
          </div>
        </>
      ) : f ? (
        <>
          <div class="iv-retour">
            <button
              type="button"
              class="iv-link"
              onClick={() => setVueListe(true)}
            >
              ← Tout l’inventaire
            </button>
            <span>
              {edition.item
                ? `Version ${edition.item.version}`
                : 'Nouvel élément'}
              {modifie ? ' · Modifications en cours' : ''}
            </span>
          </div>
          <div class="iv-editor">
            <div class="iv-editor-header">
              <span class={'iv-icone iv-icone-' + f.type}>
                <IconeInventaire type={f.type} taille={28} />
              </span>
              <div>
                <p class="iv-eyebrow">{TYPES_INVENTAIRE[f.type]}</p>
                <h2>{f.nom || 'Un nouvel élément'}</h2>
              </div>
              {!f.actif ? <span class="iv-badge">Inactif</span> : null}
            </div>
            <nav class="iv-tabs" aria-label="Configuration de l’élément">
              {[
                { cle: 'fiche', nom: 'Élément et tarif' },
                {
                  cle: 'disponibilites',
                  nom: f.nature === 'achat' ? 'Conditions' : 'Disponibilités',
                },
                { cle: 'composition', nom: 'Subdivisions et liens' },
              ].map((t) => (
                <button
                  type="button"
                  aria-current={onglet === t.cle ? 'page' : undefined}
                  onClick={() => setOnglet(t.cle as typeof onglet)}
                >
                  {t.nom}
                </button>
              ))}
            </nav>
            <fieldset disabled={!modifiable || occupe} class="iv-fields">
              {onglet === 'fiche' ? (
                <>
                  <div class="iv-section-intro">
                    <h3>Sa place dans votre inventaire</h3>
                    <p>
                      Les prix et les comptes GL accompagneront les lignes de
                      réservation ou d’achat.
                    </p>
                  </div>
                  <div class="iv-deux">
                    <label>
                      Nom de l’élément
                      <input
                        value={f.nom}
                        maxLength={120}
                        placeholder="Ex. Salle des Érables, projecteur…"
                        onInput={(e) => changer({ nom: e.currentTarget.value })}
                      />
                    </label>
                    <label>
                      Catégorie
                      <input
                        value={f.categorie ?? ''}
                        maxLength={100}
                        list="iv-categories"
                        placeholder="Ex. Salles, audiovisuel…"
                        onInput={(e) =>
                          changer({ categorie: e.currentTarget.value })
                        }
                      />
                      <datalist id="iv-categories">
                        {[
                          ...new Set(
                            donnees.items
                              .map((i) => i.fiche.categorie)
                              .filter(Boolean),
                          ),
                        ].map((c) => (
                          <option value={c} />
                        ))}
                      </datalist>
                    </label>
                  </div>
                  <label>
                    Description
                    <textarea
                      value={f.description}
                      rows={3}
                      placeholder="Ce qui est inclus, à qui cet élément convient…"
                      onInput={(e) =>
                        changer({ description: e.currentTarget.value })
                      }
                    />
                  </label>
                  <div class="iv-trois">
                    <label>
                      Type
                      <select
                        aria-label="Type"
                        value={f.type}
                        disabled={Boolean(edition.item)}
                        onChange={(e) => {
                          const t = e.currentTarget
                            .value as FicheInventaire['type']
                          changer({
                            type: t,
                            nature:
                              t === 'activite' ? 'reservation' : 'location',
                            unite: t === 'activite' ? 'personne' : 'heure',
                            quantite: t === 'activite' ? 30 : 1,
                          })
                        }}
                      >
                        {Object.entries(TYPES_INVENTAIRE).map(([k, v]) => (
                          <option value={k}>{v}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Utilisation
                      <select
                        aria-label="Utilisation"
                        value={f.nature}
                        disabled={Boolean(edition.item)}
                        onChange={(e) =>
                          changer({
                            nature: e.currentTarget
                              .value as FicheInventaire['nature'],
                            ...(e.currentTarget.value === 'achat' &&
                            ['heure', 'jour'].includes(f.unite)
                              ? { unite: 'unite' as const }
                              : {}),
                          })
                        }
                      >
                        <option value="location">Location par créneau</option>
                        <option value="reservation">
                          Réservation / inscription
                        </option>
                        <optgroup label="Autre usage">
                          <option value="achat">Vente sans créneau</option>
                        </optgroup>
                      </select>
                    </label>
                    <label>
                      {f.nature === 'achat'
                        ? 'Limite par achat'
                        : f.type === 'activite'
                          ? 'Nombre de places'
                          : 'Quantité disponible sur un même créneau'}
                      <input
                        type="number"
                        min={1}
                        max={100000}
                        value={f.quantite}
                        onInput={(e) =>
                          changer({ quantite: Number(e.currentTarget.value) })
                        }
                      />
                      {f.nature === 'achat' ? (
                        <small>
                          Unités maximales par commande. Le stock physique n’est
                          pas suivi.
                        </small>
                      ) : null}
                    </label>
                  </div>
                  {f.nature !== 'achat' ? (
                    <div class="iv-temporalite">
                      <strong>Une disponibilité liée à la période</strong>
                      <p>
                        La quantité est partagée entre les réservations qui se
                        chevauchent. Après la fin du créneau, les unités
                        redeviennent disponibles pour une autre réservation.
                      </p>
                      <button
                        type="button"
                        class="iv-link"
                        onClick={() => setOnglet('disponibilites')}
                      >
                        Régler les jours, heures et durées →
                      </button>
                    </div>
                  ) : null}
                  <section class="iv-pricing">
                    <div class="iv-section-intro">
                      <h3>Prix et compte GL sur la même ligne</h3>
                      <p>
                        Le compte de revenus identifie l’élément loué dans les
                        écritures. Les taxes et le compte d’encaissement
                        complètent la comptabilisation.
                      </p>
                    </div>
                    <div class="iv-ligne-prix">
                      <div>
                        <small>Élément</small>
                        <strong>{f.nom || 'Votre élément'}</strong>
                      </div>
                      <label>
                        Prix ($ CA)
                        <input
                          type="number"
                          min={0}
                          max={1000000}
                          step="0.01"
                          value={f.prix_cents / 100}
                          onInput={(e) =>
                            changer({
                              prix_cents: Math.round(
                                Number(e.currentTarget.value) * 100,
                              ),
                            })
                          }
                        />
                      </label>
                      <label>
                        Unité
                        <select
                          aria-label="Unité de facturation"
                          value={f.unite}
                          onChange={(e) =>
                            changer({
                              unite: e.currentTarget
                                .value as FicheInventaire['unite'],
                            })
                          }
                        >
                          {Object.entries(UNITES_INVENTAIRE)
                            .filter(
                              ([k]) =>
                                f.nature !== 'achat' ||
                                !['heure', 'jour'].includes(k),
                            )
                            .map(([k, v]) => (
                              <option value={k}>{v}</option>
                            ))}
                        </select>
                      </label>
                      <label>
                        Compte GL — revenus
                        <select
                          aria-label="Compte GL — revenus"
                          disabled={!comptable}
                          value={f.compte_gl?.compte_credit ?? ''}
                          onChange={(e) =>
                            e.currentTarget.value
                              ? changerGL({
                                  compte_credit: e.currentTarget.value,
                                })
                              : changer({ compte_gl: undefined })
                          }
                        >
                          <option value="">Choisir un compte…</option>
                          {comptesActifs
                            .filter((c) =>
                              ['produit', 'passif'].includes(c.type),
                            )
                            .map((c) => (
                              <option value={c.code}>
                                {c.code} · {c.libelle}
                              </option>
                            ))}
                        </select>
                      </label>
                    </div>
                    {!comptesActifs.length ? (
                      <p class="iv-aide">
                        Ajoutez ou importez la charte de votre organisation dans
                        Finances pour associer les comptes.
                      </p>
                    ) : null}
                    {f.compte_gl ? (
                      <fieldset disabled={!comptable} class="iv-gl-details">
                        <div class="iv-deux">
                          <label>
                            Compte d’encaissement
                            <select
                              aria-label="Compte d’encaissement"
                              value={f.compte_gl.compte_debit}
                              onChange={(e) =>
                                changerGL({
                                  compte_debit: e.currentTarget.value,
                                })
                              }
                            >
                              <option value="">
                                Choisir le compte de débit…
                              </option>
                              {comptesActifs
                                .filter((c) => c.type === 'actif')
                                .map((c) => (
                                  <option value={c.code}>
                                    {c.code} · {c.libelle}
                                  </option>
                                ))}
                            </select>
                          </label>
                          <label>
                            Traitement
                            <select
                              aria-label="Traitement comptable"
                              value={f.compte_gl.traitement}
                              onChange={(e) =>
                                changerGL({
                                  traitement: e.currentTarget
                                    .value as CompteGlItem['traitement'],
                                })
                              }
                            >
                              <option value="produit">Revenus</option>
                              <option value="differe">Revenus reportés</option>
                            </select>
                          </label>
                        </div>
                        {donnees.taxes?.length ? (
                          <div class="iv-deux">
                            {donnees.taxes.map((t) => (
                              <label key={t.nom}>
                                Compte de taxe — {t.nom}
                                <select
                                  aria-label={'Compte de taxe — ' + t.nom}
                                  value={f.compte_gl?.taxes[t.nom] ?? ''}
                                  onChange={(e) =>
                                    changerGL({
                                      taxes: {
                                        ...f.compte_gl!.taxes,
                                        [t.nom]: e.currentTarget.value,
                                      },
                                    })
                                  }
                                >
                                  <option value="">Choisir un compte…</option>
                                  {comptesActifs
                                    .filter((c) => c.type === 'passif')
                                    .map((c) => (
                                      <option value={c.code}>
                                        {c.code} · {c.libelle}
                                      </option>
                                    ))}
                                </select>
                              </label>
                            ))}
                          </div>
                        ) : null}
                      </fieldset>
                    ) : null}
                    {!comptable ? (
                      <p class="iv-aide">
                        Votre rôle permet de consulter les comptes ; une
                        personne autorisée à configurer les finances peut les
                        modifier.
                      </p>
                    ) : null}
                  </section>
                  <label class="iv-checkbox">
                    <input
                      type="checkbox"
                      checked={f.actif}
                      onChange={(e) =>
                        changer({ actif: e.currentTarget.checked })
                      }
                    />
                    Élément actif et proposable aux clients
                  </label>
                </>
              ) : onglet === 'disponibilites' ? (
                f.nature === 'achat' ? (
                  <div class="iv-info">
                    <h3>Un achat sans réservation de créneau</h3>
                    <p>
                      Son parcours client peut recueillir les coordonnées,
                      proposer des éléments supplémentaires et demander le
                      paiement. Les formulaires et contrats restent
                      personnalisables. La quantité limite chaque commande ;
                      elle ne représente pas un stock physique décompté à chaque
                      vente.
                    </p>
                  </div>
                ) : (
                  <>
                    <div class="iv-section-intro">
                      <h3>Les disponibilités de référence</h3>
                      <p>
                        Elles sont reprises lorsque vous créez un parcours pour
                        cet élément. Chaque offre peut ensuite affiner ses
                        conditions sans changer les réservations existantes.
                      </p>
                    </div>
                    {f.type === 'activite' ? (
                      <>
                        <div class="iv-trois">
                          <label>
                            Date de l’activité
                            <input
                              type="date"
                              value={dispo.date}
                              onInput={(e) =>
                                changerDispo({
                                  date: e.currentTarget.value || undefined,
                                })
                              }
                            />
                          </label>
                          <label>
                            Début
                            <input
                              type="time"
                              value={dispo.debut}
                              onInput={(e) =>
                                changerDispo({ debut: e.currentTarget.value })
                              }
                            />
                          </label>
                          <label>
                            Fin
                            <input
                              type="time"
                              value={dispo.fin}
                              onInput={(e) =>
                                changerDispo({ fin: e.currentTarget.value })
                              }
                            />
                          </label>
                        </div>
                        <label>
                          Personnes par inscription
                          <input
                            type="number"
                            min={1}
                            max={f.quantite}
                            value={dispo.maximum_par_inscription}
                            onInput={(e) =>
                              changerDispo({
                                maximum_par_inscription: Number(
                                  e.currentTarget.value,
                                ),
                                capacite: f.quantite,
                              })
                            }
                          />
                        </label>
                      </>
                    ) : (
                      <>
                        <label>Jours proposés</label>
                        <div class="iv-jours">
                          {JOURS.map((j) => (
                            <button
                              type="button"
                              aria-pressed={dispo.jours.includes(j.cle)}
                              onClick={() =>
                                changerDispo({
                                  jours: dispo.jours.includes(j.cle)
                                    ? dispo.jours.filter((d) => d !== j.cle)
                                    : [...dispo.jours, j.cle],
                                })
                              }
                            >
                              {j.nom}
                            </button>
                          ))}
                        </div>
                        <div class="iv-deux">
                          <label>
                            Ouverture
                            <input
                              type="time"
                              value={dispo.ouverture}
                              onInput={(e) =>
                                changerDispo({
                                  ouverture: e.currentTarget.value,
                                })
                              }
                            />
                          </label>
                          <label>
                            Fermeture
                            <input
                              type="time"
                              value={
                                dispo.fermeture === '24:00'
                                  ? '23:59'
                                  : dispo.fermeture
                              }
                              onInput={(e) =>
                                changerDispo({
                                  fermeture: e.currentTarget.value,
                                })
                              }
                            />
                            <small>
                              {dispo.fermeture === '24:00'
                                ? 'Minuit, en fin de journée.'
                                : ''}
                            </small>
                          </label>
                        </div>
                        <div class="iv-deux">
                          <label>
                            Durée minimale
                            <select
                              aria-label="Durée minimale"
                              value={dispo.duree_minimum}
                              onChange={(e) =>
                                changerDispo({
                                  duree_minimum: Number(e.currentTarget.value),
                                })
                              }
                            >
                              {[
                                ...new Set([
                                  15,
                                  30,
                                  60,
                                  90,
                                  120,
                                  180,
                                  240,
                                  480,
                                  1440,
                                  dispo.duree_minimum,
                                ]),
                              ]
                                .sort((a, b) => a - b)
                                .map((n) => (
                                  <option value={n}>
                                    {n < 60 ? n + ' min' : n / 60 + ' h'}
                                  </option>
                                ))}
                            </select>
                          </label>
                          <label>
                            Durée maximale
                            <select
                              aria-label="Durée maximale"
                              value={dispo.duree_maximum}
                              onChange={(e) =>
                                changerDispo({
                                  duree_maximum: Number(e.currentTarget.value),
                                })
                              }
                            >
                              {[
                                ...new Set([
                                  30,
                                  60,
                                  90,
                                  120,
                                  180,
                                  240,
                                  480,
                                  720,
                                  1440,
                                  dispo.duree_maximum,
                                ]),
                              ]
                                .filter((n) => n >= dispo.duree_minimum)
                                .sort((a, b) => a - b)
                                .map((n) => (
                                  <option value={n}>
                                    {n < 60 ? n + ' min' : n / 60 + ' h'}
                                  </option>
                                ))}
                            </select>
                          </label>
                        </div>
                        <p class="iv-muted" aria-live="polite">
                          Départs toutes les {dispo.pas_minutes} minutes ·
                          durées de {dispo.duree_minimum} à{' '}
                          {dispo.duree_maximum} minutes. La grille et les durées
                          s’ajustent ensemble.
                        </p>
                        <details>
                          <summary>Affiner la grille des horaires</summary>
                          <div class="iv-deux">
                            <label>
                              Intervalle entre deux départs
                              <select
                                aria-label="Intervalle entre les départs"
                                value={dispo.pas_minutes}
                                onChange={(e) =>
                                  changerDispo({
                                    pas_minutes: Number(e.currentTarget.value),
                                  })
                                }
                              >
                                {[
                                  ...new Set([
                                    5,
                                    10,
                                    15,
                                    20,
                                    30,
                                    60,
                                    120,
                                    dispo.pas_minutes,
                                  ]),
                                ]
                                  .sort((a, b) => a - b)
                                  .map((n) => (
                                    <option value={n}>{n} min</option>
                                  ))}
                              </select>
                            </label>
                            <label>
                              Réservations jusqu’à (jours d’avance)
                              <input
                                type="number"
                                min={1}
                                max={365}
                                value={dispo.horizon_jours}
                                onInput={(e) =>
                                  changerDispo({
                                    horizon_jours: Number(
                                      e.currentTarget.value,
                                    ),
                                  })
                                }
                              />
                            </label>
                          </div>
                        </details>
                      </>
                    )}
                  </>
                )
              ) : (
                <>
                  <div class="iv-section-intro">
                    <h3>Salles divisibles et espaces partagés</h3>
                    <p>
                      Reliez la salle complète à chacune de ses parties. Chaque
                      lien bloque seulement les deux espaces concernés.
                    </p>
                  </div>
                  {!edition.item ? (
                    <p class="iv-info">
                      Enregistrez d’abord cet élément pour le relier aux autres
                      espaces.
                    </p>
                  ) : f.type !== 'espace' ? (
                    <p class="iv-info">
                      Les subdivisions concernent les espaces. Les quantités
                      d’équipement se règlent dans Élément et tarif.
                    </p>
                  ) : (
                    <>
                      {f.nature !== 'achat' && <SubdivisionInventaire
                        key={edition.item.id}
                        item={edition.item}
                        donnees={donnees}
                        baseUrl={baseUrl}
                        erreur={faute}
                        termine={async () => {
                          await charger()
                          setNotice('Subdivision enregistrée. La salle complète et ses sections se bloquent dans les deux sens; les sections peuvent se réserver séparément.')
                        }}
                      />}
                      <h4>Liens enregistrés de « {f.nom} »</h4>
                      {relations.length ? (
                        <div class="iv-relations">
                          {relations.map((r) => {
                            const autre = donnees.items.find(
                              (i) =>
                                i.id ===
                                (r.item_a === edition.item?.id
                                  ? r.item_b
                                  : r.item_a),
                            )
                            return (
                              <div key={r.id}>
                                <span class="iv-icone">
                                  <IconeInventaire type="espace" taille={20} />
                                </span>
                                <span>
                                  <strong>
                                    {autre?.fiche.nom ?? 'Espace lié'}
                                  </strong>
                                  <small>
                                    {r.mode === 'journee'
                                      ? 'Indisponibilité sur la journée entière'
                                      : 'Indisponibilité sur les créneaux qui se chevauchent'}
                                  </small>
                                </span>
                                <button
                                  type="button"
                                  class="iv-link"
                                  onClick={() => void retirerLien(r.id)}
                                >
                                  Retirer le lien
                                </button>
                              </div>
                            )
                          })}
                        </div>
                      ) : (
                        <p class="iv-aide">Aucun espace lié pour le moment.</p>
                      )}
                      {!liaisonOuverte ? (
                        <button
                          type="button"
                          class="iv-button"
                          onClick={() => setLiaisonOuverte(true)}
                        >
                          + Ajouter un lien entre espaces
                        </button>
                      ) : (
                        <div class="iv-ajouter-liens">
                          <label>
                            Quand l’un est réservé
                            <select
                              aria-label="Période de blocage"
                              value={modeLien}
                              onChange={(e) =>
                                setModeLien(
                                  e.currentTarget.value as typeof modeLien,
                                )
                              }
                            >
                              <option value="journee">
                                Bloquer l’autre pour toute la journée
                              </option>
                              <option value="chevauchement">
                                Bloquer seulement les heures qui se chevauchent
                              </option>
                            </select>
                          </label>
                          <label>
                            Rechercher un espace à relier
                            <input
                              type="search"
                              value={rechercheEspaces}
                              placeholder="Nom de salle ou de catégorie…"
                              onInput={(e) =>
                                setRechercheEspaces(e.currentTarget.value)
                              }
                            />
                          </label>
                          <div class="iv-parties">
                            {espacesALier.map((i) => (
                              <label class="iv-checkbox">
                                <input
                                  type="checkbox"
                                  checked={parties.includes(i.id)}
                                  onChange={(e) =>
                                    setParties(
                                      e.currentTarget.checked
                                        ? [...parties, i.id]
                                        : parties.filter((p) => p !== i.id),
                                    )
                                  }
                                />
                                <span>{i.fiche.nom}</span>
                              </label>
                            ))}
                          </div>
                          {espacesALier.length === 0 && (
                            <p class="iv-aide">
                              {rechercheEspaces
                                ? 'Aucun espace ne correspond à votre recherche.'
                                : 'Tous les espaces actifs sont déjà liés.'}
                            </p>
                          )}
                          <div class="iv-liaison-actions">
                            <button
                              type="button"
                              class="iv-button"
                              disabled={!parties.length || occupe}
                              onClick={() => void associer()}
                            >
                              Relier les espaces sélectionnés
                              {parties.length > 0 ? ` (${parties.length})` : ''}
                            </button>
                            {relations.length > 0 && (
                              <button
                                type="button"
                                class="iv-link"
                                onClick={() => {
                                  setLiaisonOuverte(false)
                                  setParties([])
                                  setRechercheEspaces('')
                                }}
                              >
                                Annuler
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </fieldset>
            <div class="iv-editor-footer">
              <p>
                {modifie
                  ? 'Modifications non enregistrées'
                  : edition.item
                    ? 'Élément enregistré'
                    : 'Enregistrez l’élément pour créer son parcours.'}
              </p>
              {edition.item && !modifie && modifiable ? (
                <button
                  type="button"
                  class="iv-button"
                  onClick={() => onParcours(edition.item!)}
                >
                  Créer son parcours →
                </button>
              ) : null}
              {modifiable ? (
                <button
                  type="button"
                  class="iv-primary"
                  disabled={occupe || Boolean(edition.item && !modifie)}
                  onClick={() => void enregistrer()}
                >
                  {occupe ? 'Enregistrement…' : 'Enregistrer l’élément'}
                </button>
              ) : null}
            </div>
          </div>
        </>
      ) : null}
    </div>
  )
}
export const stylesInventaire = `
.iv-temporalite{border:1px solid #ece1f3;border-radius:12px;padding:19px 21px;background:#faf8fc;margin:5px 0 26px}.iv-temporalite strong{font-size:14px}.iv-temporalite p{color:#7d6985;font-size:12px;line-height:1.7;margin:8px 0 13px;max-width:730px}.iv-horaires{font-size:11px;margin-top:7px}.iv-root input[type=checkbox]{min-height:0!important;min-width:0!important}.iv-ajouter-liens{border:1px solid var(--iv-ligne);border-radius:12px;padding:20px;margin-top:20px;background:#fcfafd}.iv-ajouter-liens .iv-parties{max-height:330px;overflow:auto;padding:3px}.iv-liaison-actions{display:flex;flex-wrap:wrap;gap:15px;align-items:center}.iv-ajouter-liens .iv-parties label{background:#fff;margin-bottom:0}
.iv-root{--iv-encre:#2a1632;--iv-gris:#6b5a70;--iv-bleu:#391d44;--iv-ligne:#ece2f0;color:var(--iv-encre);font:14px/1.5 -apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;min-width:0}.iv-root *{box-sizing:border-box}.iv-root h2,.iv-root h3,.iv-root h4,.iv-root p{margin:0}.iv-root button,.iv-root input,.iv-root select,.iv-root textarea{font:inherit}.iv-root button{cursor:pointer}.iv-root button:disabled{cursor:default;opacity:.45}.iv-root label:not(.iv-checkbox){display:flex;flex-direction:column;font-size:13px;gap:8px;color:#4e4153;min-width:0;margin-bottom:20px}.iv-root input:not([type=checkbox]),.iv-root textarea,.iv-root select{width:100%;min-width:0;min-height:44px;border:1px solid #dcd3df;border-radius:10px;padding:11px 12px;color:var(--iv-encre);background:white}.iv-root textarea{resize:vertical}.iv-root input[type=checkbox]{width:18px;height:18px;accent-color:var(--iv-bleu);flex-shrink:0}.iv-root fieldset{border:0;padding:0;margin:0;min-width:0}.iv-root :focus-visible{outline:3px solid #c4c5ee;outline-offset:3px}.iv-button,.iv-primary{border:1px solid #e2dae4;padding:11px 16px;min-height:44px;border-radius:10px;background:white;white-space:nowrap}.iv-primary{background:var(--iv-bleu);border-color:var(--iv-bleu);color:white}.iv-link{padding:4px 0;border:0;background:none;color:var(--iv-bleu);font-size:13px}.iv-entete{display:flex;align-items:center;justify-content:space-between;gap:25px;margin-bottom:28px}.iv-entete h2{font-size:24px;letter-spacing:-.6px}.iv-entete p{font-size:13px;color:var(--iv-gris);margin-top:9px;max-width:630px}.iv-resume{display:flex;gap:50px;margin:28px 0;padding:22px 0;border-top:1px solid var(--iv-ligne);border-bottom:1px solid var(--iv-ligne)}.iv-resume>div{display:flex;align-items:baseline;gap:10px}.iv-resume strong{font-size:26px;letter-spacing:-.5px}.iv-resume span{font-size:12px;color:var(--iv-gris)}.iv-outils{display:flex;align-items:center;gap:15px;margin-bottom:20px}.iv-root .iv-outils label{margin:0}.iv-recherche{flex:1;max-width:500px}.iv-root .iv-outils label>span{font-size:11px;color:var(--iv-gris)}.iv-checkbox{display:flex;align-items:center;gap:9px;font-size:13px;cursor:pointer}.iv-outils .iv-checkbox{align-self:flex-end;margin-bottom:10px!important;white-space:nowrap}.iv-table-wrap{border:1px solid var(--iv-ligne);border-radius:15px;background:white;overflow:auto}.iv-table{border-collapse:collapse;width:100%;font-size:13px;text-align:left}.iv-table th{font-size:11px;font-weight:500;color:var(--iv-gris);padding:16px 18px;background:#fbfafc;border-bottom:1px solid var(--iv-ligne);white-space:nowrap}.iv-table td{padding:19px 18px;border-bottom:1px solid #f2ebf5;vertical-align:middle}.iv-table tr:last-child td{border-bottom:0}.iv-table small{font-size:11px;line-height:1.5;color:var(--iv-gris);display:block;margin-top:5px}.iv-article{display:flex;align-items:center;gap:13px;background:none;border:0;padding:0;text-align:left;color:var(--iv-encre)}.iv-article strong{font-size:14px;line-height:1.5}.iv-icone{display:grid;place-items:center;width:43px;height:43px;border-radius:12px;background:#f7f1fa;color:#391d44;flex-shrink:0}.iv-icone-equipement{background:#f7f3f9;color:#4f8774}.iv-icone-produit{background:#f8f1e8;color:#a2804e}.iv-icone-activite{background:#f4edf8;color:#391d44}.iv-compte{border:1px solid #ece2f0;border-radius:7px;background:#fbf9fd;color:#715f78;padding:5px 8px;font-size:12px;white-space:nowrap}.iv-a-completer{color:#977128;background:#fcf8ee;border-color:#eee3c9}.iv-petit{font-size:11px;padding:8px 10px;min-height:35px}.iv-muted{font-size:12px;color:var(--iv-gris)}.iv-explication{display:flex;gap:15px;padding:23px;margin-top:25px;border:1px solid var(--iv-ligne);border-radius:13px;background:#fcfafd}.iv-explication strong{font-size:14px}.iv-explication p{font-size:12px;color:var(--iv-gris);max-width:760px;margin-top:7px}.iv-retour{display:flex;justify-content:space-between;align-items:center;gap:20px;margin:5px 0 24px}.iv-retour>span{font-size:11px;color:var(--iv-gris)}.iv-editor{background:white;border:1px solid var(--iv-ligne);border-radius:17px;overflow:hidden}.iv-editor-header{display:flex;align-items:center;gap:16px;padding:29px 32px}.iv-editor-header h2{font-size:25px;letter-spacing:-.6px;overflow-wrap:anywhere}.iv-eyebrow{font-size:10px;letter-spacing:1.3px;text-transform:uppercase;color:var(--iv-gris);margin-bottom:5px!important}.iv-tabs{display:flex;gap:6px;padding:0 32px 17px;border-bottom:1px solid var(--iv-ligne)}.iv-tabs button{background:none;border:0;padding:11px 16px;color:var(--iv-gris);font-size:13px;border-radius:9px}.iv-tabs button[aria-current]{background:#f7f2fa;color:var(--iv-bleu);font-weight:600}.iv-root .iv-fields{padding:30px 32px}.iv-section-intro{margin:0 0 25px}.iv-section-intro h3{font-size:18px;letter-spacing:-.35px}.iv-section-intro p{font-size:12px;color:var(--iv-gris);margin-top:9px;max-width:740px;line-height:1.65}.iv-deux{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:22px}.iv-trois{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:22px}.iv-pricing{border-top:1px solid var(--iv-ligne);margin:15px 0 25px;padding-top:28px}.iv-ligne-prix{display:grid;grid-template-columns:minmax(110px,1.1fr) minmax(90px,.7fr) minmax(110px,.8fr) minmax(160px,1.4fr);gap:15px;border:1px solid var(--iv-ligne);border-radius:12px;padding:20px;background:#fcfafd;align-items:center}.iv-ligne-prix>div{display:flex;flex-direction:column;gap:8px}.iv-ligne-prix small{font-size:11px;color:var(--iv-gris)}.iv-ligne-prix strong{font-size:13px;overflow-wrap:anywhere}.iv-ligne-prix label{margin:0!important}.iv-root .iv-gl-details{padding:23px 0 0}.iv-aide{font-size:12px;color:var(--iv-gris);margin:15px 0!important;line-height:1.65}.iv-editor-footer{display:flex;gap:12px;align-items:center;padding:22px 32px;border-top:1px solid var(--iv-ligne);background:#fefdfe}.iv-editor-footer>p{flex:1;color:var(--iv-gris);font-size:11px}.iv-vide{padding:65px 25px;text-align:center;background:white;border:1px dashed #e5d5ef;border-radius:15px}.iv-vide h3{font-size:20px}.iv-vide p{font-size:13px;color:var(--iv-gris);margin:12px 0 20px}.iv-alert,.iv-notice,.iv-info{border-radius:11px;padding:17px 20px;margin-bottom:20px!important;font-size:13px;line-height:1.7;background:#f9f5fb;color:#391d44}.iv-alert{background:#fff2f0;color:#a34439}.iv-notice{background:#eef8f3;color:#347258}.iv-info h3{font-size:17px;margin-bottom:12px}.iv-composition-schema{background:#fbf9fd;border:1px solid var(--iv-ligne);border-radius:13px;padding:25px;text-align:center;margin-bottom:30px}.iv-composition-schema>span{display:inline-block;background:#f7f2fa;border:1px solid #e7d7f0;padding:11px 40px;border-radius:9px;color:#391d44;font-size:14px}.iv-composition-schema>div{display:flex;justify-content:center;gap:18px;margin:21px 0}.iv-composition-schema>div>span{padding:11px 22px;background:white;border:1px solid var(--iv-ligne);border-radius:9px;font-size:13px}.iv-composition-schema p{font-size:12px;color:var(--iv-gris);line-height:1.7}.iv-relations{margin:14px 0 25px}.iv-relations>div{display:flex;align-items:center;gap:12px;padding:17px 0;border-bottom:1px solid var(--iv-ligne)}.iv-relations>div>span:nth-child(2){flex:1}.iv-relations strong,.iv-relations small{display:block}.iv-relations strong{font-size:13px}.iv-relations small{font-size:11px;color:var(--iv-gris);margin-top:5px}.iv-parties{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px;margin-bottom:20px}.iv-parties label{border:1px solid var(--iv-ligne);border-radius:10px;padding:14px}.iv-jours{display:flex;gap:8px;margin:0 0 25px;flex-wrap:wrap}.iv-jours button{border:1px solid #e9dcf2;border-radius:9px;min-width:45px;min-height:42px;background:white;color:#6b5a70;font-size:12px}.iv-jours button[aria-pressed=true]{background:#f7f2fa;border-color:#c3c4ee;color:var(--iv-bleu)}.iv-root summary{cursor:pointer;color:var(--iv-gris);font-size:13px;margin:15px 0 20px}.iv-reprise{display:flex;justify-content:space-between;gap:15px;padding:15px 20px;background:#f7f2fa;border-radius:10px;margin-bottom:20px;font-size:12px}.iv-reprise button{background:none;border:0;color:var(--iv-bleu)}.iv-badge{margin-left:auto;font-size:11px;color:#6b5a70;background:#f5f0f7;border-radius:6px;padding:6px 10px}.iv-sr{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0)}@media(max-width:1250px){.iv-table th,.iv-table td{padding:15px 12px}.iv-resume{gap:25px}.iv-resume>div{flex-direction:column;gap:2px}.iv-ligne-prix{grid-template-columns:repeat(2,minmax(0,1fr))}}@media(max-width:800px){.iv-table{display:block}.iv-table thead{display:none}.iv-table tbody{display:grid;grid-template-columns:repeat(2,minmax(0,1fr))}.iv-table tr{display:flex;flex-wrap:wrap;align-items:center;gap:10px;padding:20px;border-bottom:1px solid var(--iv-ligne);min-width:0}.iv-table td{border:0;padding:0;flex:1;min-width:0}.iv-table td:first-child,.iv-table td:last-child{flex-basis:100%}.iv-table td:first-child{margin-bottom:8px}.iv-table td:nth-child(4){flex-basis:100%;margin:7px 0}.iv-table td:nth-child(4)>small{display:inline;margin-left:8px}.iv-table td:last-child{margin-top:8px}.iv-trois{grid-template-columns:1fr 1fr}.iv-trois>label:last-child{grid-column:1/-1}.iv-parties{grid-template-columns:1fr}}@media(max-width:600px){.iv-entete{flex-direction:column;align-items:flex-start}.iv-entete>button{width:100%}.iv-entete h2{font-size:22px}.iv-table tbody{grid-template-columns:1fr}.iv-resume{gap:15px;justify-content:space-between}.iv-resume span{font-size:10px;max-width:85px}.iv-resume strong{font-size:25px}.iv-outils{flex-wrap:wrap}.iv-recherche{flex-basis:100%;max-width:none}.iv-outils>label:nth-child(2){flex:1}.iv-table td:last-child>button{width:100%}.iv-editor-header{padding:23px 20px}.iv-editor-header h2{font-size:22px}.iv-tabs{padding:0 10px 12px;justify-content:space-between;gap:2px}.iv-tabs button{padding:10px 9px;font-size:11px}.iv-root .iv-fields{padding:25px 20px}.iv-deux,.iv-trois,.iv-ligne-prix{grid-template-columns:1fr;gap:0}.iv-ligne-prix{gap:18px;padding:17px}.iv-root input:not([type=checkbox]),.iv-root select,.iv-root textarea{font-size:16px}.iv-editor-footer{padding:17px 20px;flex-wrap:wrap}.iv-editor-footer>p{flex-basis:100%;margin-bottom:5px}.iv-editor-footer>button{flex:1;font-size:12px}.iv-composition-schema{padding:22px 12px}.iv-composition-schema>div{gap:9px}.iv-composition-schema>div>span{font-size:11px;padding:10px}.iv-relations>div{flex-wrap:wrap}.iv-relations>div>button{margin-left:54px}.iv-retour>span{font-size:10px}.iv-explication{padding:19px;align-items:flex-start}.iv-root .iv-jours{gap:5px}.iv-jours button{min-width:32px;flex:1}.iv-composition-schema>span{padding:11px 20px}}
`
